
import { SwingTendency } from '@/components/putting/training/charts/swing/types';
import { supabase } from "@/integrations/supabase/client";

/**
 * Delete data from either swing_tendencies, level_tests_results or putting_test_results table
 * based on the provided ID
 */
export const deleteDriverTendency = async (id: string): Promise<{ success: boolean; error?: any }> => {
  try {
    console.log("Attempting to delete driver tendency with ID:", id);
    
    // First try deleting from swing_tendencies
    const { data: swingData, error: swingError } = await supabase
      .from('swing_tendencies')
      .delete()
      .eq('id', id);
    
    // If there was no error, we assume the record was deleted
    if (!swingError) {
      console.log("Successfully deleted from swing_tendencies");
      return { success: true };
    } else {
      console.log("Error trying to delete from swing_tendencies:", swingError);
    }
    
    // If there was an error in swing_tendencies, try level_tests_results
    const { data: levelData, error: levelError } = await supabase
      .from('level_tests_results')
      .delete()
      .eq('id', id);
    
    // If there was no error, we assume the record was deleted
    if (!levelError) {
      console.log("Successfully deleted from level_tests_results");
      return { success: true };
    } else {
      console.log("Error trying to delete from level_tests_results:", levelError);
    }
    
    // Try deleting from putting_test_results as last resort
    const { data: puttingData, error: puttingError } = await supabase
      .from('putting_test_results')
      .delete()
      .eq('id', id);
    
    // If there was no error, we assume the record was deleted
    if (!puttingError) {
      console.log("Successfully deleted from putting_test_results");
      return { success: true };
    } else {
      console.log("Error trying to delete from putting_test_results:", puttingError);
    }
    
    // If we get here, we encountered errors in all deletion attempts
    console.log("Encountered errors in all deletion attempts");
    return { 
      success: false, 
      error: { 
        message: "L'enregistrement n'a pas été trouvé ou n'a pas pu être supprimé",
        details: { swingError, levelError, puttingError }
      } 
    };
  } catch (err) {
    console.error("Unexpected error deleting tendency:", err);
    return { success: false, error: err };
  }
};

/**
 * Enhance tendencies with calculated points if they're missing
 * and ensure test_level is never null
 */
export const enhanceTendenciesWithPoints = (tendencies: SwingTendency[]): SwingTendency[] => {
  return tendencies.map(t => {
    let updatedTendency = { ...t };
    
    // Ensure points is a number
    if (updatedTendency.points === undefined || updatedTendency.points === null) {
      // Calculate points based on tendency if missing
      let calculatedPoints = 0;
      if (updatedTendency.tendency === 'center') calculatedPoints = 5;
      else if (updatedTendency.tendency === 'left' || updatedTendency.tendency === 'right') calculatedPoints = 2;
      
      updatedTendency.points = calculatedPoints;
    }
    
    // Ensure test_level is never null
    if (updatedTendency.test_level === undefined || updatedTendency.test_level === null || updatedTendency.test_level.trim() === '') {
      // Try to extract level from test_id (e.g. "driver-l1" -> "1")
      const levelMatch = updatedTendency.test_id?.match(/-l(\d+)/i);
      if (levelMatch && levelMatch[1]) {
        updatedTendency.test_level = levelMatch[1];
      } else if (updatedTendency.club_type === 'driver') {
        // Default to level 3 for driver
        updatedTendency.test_level = '3';
      } else {
        // Default to level 3 for other club types too
        updatedTendency.test_level = '3';
      }
    }
    
    return updatedTendency;
  });
};

/**
 * Fix for the issue where training at a specific level also creates a standard entry with 5 points
 * This function will remove duplicate entries that have the same test_date but different levels
 */
export const removeDriverDuplicates = (tendencies: SwingTendency[]): SwingTendency[] => {
  // Group tendencies by date (using minute precision to match closely related entries)
  const dateGroups = new Map<string, SwingTendency[]>();
  
  // Sort tendencies by date (newest first)
  const sortedTendencies = [...tendencies].sort((a, b) => 
    new Date(b.test_date).getTime() - new Date(a.test_date).getTime()
  );
  
  // Group by date with minute precision
  sortedTendencies.forEach(tendency => {
    const date = new Date(tendency.test_date);
    const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
    
    if (!dateGroups.has(dateKey)) {
      dateGroups.set(dateKey, []);
    }
    
    dateGroups.get(dateKey)!.push(tendency);
  });
  
  // Process each group to remove duplicates
  const uniqueTendencies: SwingTendency[] = [];
  
  dateGroups.forEach(group => {
    // If the group has multiple entries
    if (group.length > 1) {
      // Find entries with level information
      const levelEntries = group.filter(t => t.test_level && t.test_level.trim() !== '');
      
      // If we have entries with level info, keep only those
      if (levelEntries.length > 0) {
        // Add all entries with level information
        uniqueTendencies.push(...levelEntries);
      } else {
        // If no level entries, add the first entry (most recent)
        uniqueTendencies.push(group[0]);
      }
    } else {
      // If only one entry in the group, add it
      uniqueTendencies.push(group[0]);
    }
  });
  
  return uniqueTendencies;
};
